import { useMemo } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { useDebouncedValue } from '@/hooks/use-debounced-value';

import { previewTimeOffRequestBalance } from '../api/time-offs.api';
import { TIME_OFF_BALANCE_PREVIEW_QUERY_KEY } from '../constants/time-offs-query-keys';
import { mapTimeOffWorkingDaysToDto } from '../mappers/time-off-request-form-payload.mapper';
import { TimeOffBalancePreview } from '../types/time-off-balance-preview';
import { TimeOffRequestDayFormValues } from '../validations/time-off-request-day-schema';

const BALANCE_PREVIEW_DEBOUNCE_MS = 400;

/** Stable text signature of the fields that affect the preview payload — RHF's `watch` returns
 *  a new array reference on every render even when the content is unchanged, so memoizing off
 *  the array itself would reset the debounce on every keystroke elsewhere on the page. */
const buildDaysSignature = (days: TimeOffRequestDayFormValues[]): string =>
  days
    .map(
      day =>
        `${day.date}|${day.isWorkingDay}|${day.portion}|${day.dayPartNumber}|${day.minutes ?? ''}`
    )
    .join(';');

/**
 * Live preview of a not-yet-submitted request's balance impact, using the exact same
 * renewal-aware projection the backend uses to accept/reject the real submission — so this
 * preview can never drift from what create actually enforces (e.g. a renewal landing mid-request
 * correctly splits pre/post-renewal balance instead of a naive current-balance subtraction).
 */
export const useTimeOffRequestBalancePreview = (
  timeOffTypeId: number | undefined,
  days: TimeOffRequestDayFormValues[]
): UseQueryResult<TimeOffBalancePreview> => {
  const daysSignature = buildDaysSignature(days);
  const mappedDays = useMemo(
    () => mapTimeOffWorkingDaysToDto(days),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `days`' relevant content is captured by daysSignature
    [daysSignature]
  );
  const debouncedDays = useDebouncedValue(mappedDays, BALANCE_PREVIEW_DEBOUNCE_MS);

  return useQuery({
    queryKey: [...TIME_OFF_BALANCE_PREVIEW_QUERY_KEY, timeOffTypeId, debouncedDays],
    queryFn: () =>
      previewTimeOffRequestBalance({ timeOffTypeId: timeOffTypeId as number, days: debouncedDays }),
    enabled: timeOffTypeId != null && debouncedDays.length > 0,
    staleTime: 0,
  });
};
